/**
 * The discard-and-retry orchestration, extracted so it can be tested for real.
 *
 * Its two money-relevant properties are invisible to a source-text assertion and both are easy to
 * break by accident:
 *
 * 1. **An unconfirmed void never replays.** `abandon` answers `false` for four different things —
 *    nothing was pending, the service's fail-closed "the payment may have completed concurrently", the
 *    service refusing to void a pending update whose kind it does not recognize, and the call outrunning
 *    its budget. None of them is "the hold is gone", and one of them may mean the buyer was just
 *    charged. Replaying there charges on top of a payment that may have landed.
 * 2. **Two presses cannot both run.** `disabled` on the button only bites once React commits, so the
 *    handler needs its own guard or two voids race two outcomes onto one screen.
 *
 * Dependency injection rather than module mocking, following `resolveHeldUpgrade`'s own
 * `deps.abandon ?? abandonHeldUpgrade` — the pattern exists here for exactly this reason (see the
 * orion-web CLAUDE.md note on why a unit test must not import the app's dependency graph).
 */

export interface DiscardAndRetryDeps<R> {
  /** Void the blocking hold. `true` ONLY when the server confirmed it; see the four causes above. */
  abandon: () => Promise<boolean>;
  /**
   * Replay the blocked action verbatim, same idempotency token. Confirmed voids only.
   *
   * Its resolved value is what the replayed action SETTLED as, and it is carried out of here unchanged.
   * A replay that is declined resolves exactly like one that goes through, so a caller that only knew
   * "replay ran" would report a declined retry as a success — which is the one thing this flow's
   * telemetry exists to tell apart.
   */
  replay: () => Promise<R>;
  /** Re-read premium + scheduled change, for the unconfirmed case where we know nothing. */
  resync: () => void;
  /** Tell the buyer we could not confirm the discard. Must not claim the payment went through. */
  notifyUnresolved: () => void;
  /** Close the escape modal. Runs before the replay so a repeat rejection can re-open it. */
  closeEscape: () => void;
  setBusy: (busy: boolean) => void;
}

/**
 * Which path ran — and on the replay path, what the replayed action settled as. `replay` is generic so
 * this module stays import-free (see the note above); the caller names its own outcome type.
 */
export type DiscardResult<R> =
  | { path: 'busy' }
  | { path: 'unresolved' }
  | { path: 'replayed'; replay: R };

/**
 * @param busy whether a discard is already running — the re-entrancy guard's input.
 * @returns which path ran AND how the replay ended, so the caller reports the OUTCOME, not the click.
 */
export async function discardAndRetry<R>(busy: boolean, deps: DiscardAndRetryDeps<R>): Promise<DiscardResult<R>> {
  if (busy) return { path: 'busy' };
  deps.setBusy(true);
  try {
    const canceled = await deps.abandon();
    deps.closeEscape();
    if (!canceled) {
      deps.notifyUnresolved();
      deps.resync();
      return { path: 'unresolved' };
    }
    return { path: 'replayed', replay: await deps.replay() };
  } finally {
    deps.setBusy(false);
  }
}
